// Pure shared boundary for guest ingestion, signup metadata, and native cloud hydration.
// Missing/invalid fields stay absent so each consumer can apply its existing defaults.
const textLimits = {
  userName: 100,
  startupName: 100,
  successMeaning: 1000,
  goal: 200,
  motivation: 200,
  founderType: 200,
  ideaAge: 200,
  userAge: 200,
  sharpestTime: 200,
  dailySessions: 200,
  focusDuration: 200,
  commitmentLevel: 200,
} as const;
const arrayKeys = ['focusKillers', 'thirtyDayGoals', 'biggestFears'] as const;

export type SanitizedOnboardingAnswers = Partial<
  Record<keyof typeof textLimits, string>
  & Record<typeof arrayKeys[number], string[]>
  & { dailyPhoneHours: number }
>;

function cleanText(value: string, maxLength: number) {
  return value.replace(/[\u0000-\u001f\u007f]/gu, ' ').replace(/\s+/gu, ' ').trim().slice(0, maxLength);
}

export function sanitizeOnboardingAnswers(value: unknown): SanitizedOnboardingAnswers {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const input = value as Record<string, unknown>;
  const result: SanitizedOnboardingAnswers = {};
  for (const key of Object.keys(textLimits) as (keyof typeof textLimits)[]) {
    if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
    const text = input[key];
    if (typeof text === 'string') {
      const cleaned = cleanText(text, textLimits[key]);
      if (cleaned) result[key] = cleaned;
    }
  }
  for (const key of arrayKeys) {
    if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
    const items = input[key];
    if (Array.isArray(items)) {
      result[key] = [...new Set(items.filter((item): item is string => typeof item === 'string')
        .map((item) => cleanText(item, 200)).filter(Boolean))].slice(0, 20);
    }
  }
  if (Object.prototype.hasOwnProperty.call(input, 'dailyPhoneHours')
      && typeof input.dailyPhoneHours === 'number' && Number.isFinite(input.dailyPhoneHours)) {
    result.dailyPhoneHours = Math.min(24, Math.max(0, input.dailyPhoneHours));
  }
  return result;
}
